import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  directionOf,
  formatCurrency,
  formatDate,
  formatNumber,
  formatTime,
  interpolate,
  localeFor,
  LANGUAGE_STORAGE_KEY,
  messagesFor,
  pickLocalized,
  resolveInitialLanguage,
  type Language,
} from './core';
import { I18nContext, type I18nValue } from './context';
import { safeStorage } from '@/lib/storage';
import { formatCounted } from './plural';

function applyDocumentLanguage(language: Language) {
  const root = document.documentElement;
  root.lang = language;
  root.dir = directionOf(language);
}

export function I18nProvider({ children, initialLanguage }: { children: ReactNode; initialLanguage?: Language }) {
  const [language, setLanguageState] = useState<Language>(() =>
    initialLanguage ??
    resolveInitialLanguage(
      safeStorage.get(LANGUAGE_STORAGE_KEY),
      typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [],
    ),
  );

  useEffect(() => {
    applyDocumentLanguage(language);
  }, [language]);

  const setLanguage = useCallback((next: Language) => {
    safeStorage.set(LANGUAGE_STORAGE_KEY, next);
    setLanguageState(next);
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      language,
      dir: directionOf(language),
      t: messagesFor(language),
      setLanguage,
      fmt: interpolate,
      count: (forms, n, values) => formatCounted(forms, n, localeFor(language), formatNumber(n, language), values),
      // Label lists: "A, B, C" / "أ، ب، ج"; sentences: "A, B and C" / "أ وب وج".
      formatList: (items, type = 'unit') =>
        type === 'unit' && language === 'ar'
          ? items.join('، ')
          : new Intl.ListFormat(language === 'ar' ? 'ar' : 'en', { style: 'long', type }).format(items),
      formatDate: (v, options, timeZone) => formatDate(v, language, options, timeZone),
      formatTime: (v, timeZone) => formatTime(v, language, timeZone),
      formatNumber: (v, options) => formatNumber(v, language, options),
      formatCurrency: (v, currency) => formatCurrency(v, language, currency),
      localized: (row, base) => pickLocalized(row, base, language),
    }),
    [language, setLanguage],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
